import React, { useEffect, useState } from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import ReactFlow, { useZoomPanHelper, useStore } from 'react-flow-renderer';
import PageFlowSideBar from '../ViewPageFlow/PageFlowSideBar';
import { CustomEdgeDown, CustomEdgeUp } from '../ViewPageFlow/CustomEdges';

// This function takes as args a project name id and the index of selected project and returns the index of selected page in localeStorage data
function returnIndexOfPageName(targetPageId, selectedProject) {
  const tempData = JSON.parse(localStorage.getItem('data'))[selectedProject].pages;

  let indexTargetPage = -1;

  tempData.forEach((page, index) => {
    if (page.pageId === targetPageId) {
      indexTargetPage = index;
    }
  });

  return indexTargetPage;
}

// This component renders all the required info of the nodes and the edges that represent a project flow diagramm.
const ProjectFlowCanvas = (props) => {
  const { selectedProject, selectedPage,loadPages } = props;

  const { setCenter } = useZoomPanHelper();
  const store = useStore();
  const [elements, setElements] = React.useState([]);
  const [initElements, setInitElements] = React.useState(false);
  const [reactflowInstance, setReactflowInstance] = useState(null);
  const colorFlows = ['#0C4D82', 'rgb(207, 216, 224)', 'pink'];

  /*
    This useEffect is runned when we load a new project flow diagramm or when we change the dimensions of our monitor. It is responsible
    to render all the nodes and the edges of a project flow diagramm.
    */

  useEffect(() => {
    if (selectedProject > -1) {
      const tempPages = JSON.parse(localStorage.getItem('data'))[selectedProject].pages;
      const { nodes } = store.getState();

      const tempElements = [];

      tempPages.forEach((page, index) => {
        tempElements.push({
          id: (index + 1).toString(),
          data: {
            label: (
              <div className="react-flow-node1">
                <div className="dot-row">
                  <span className="dot" />
                  <span className="dot" />
                  <span className="dot" />
                </div>
                <div className="react-flow-inside-box">
                  {' '}
                  {JSON.parse(localStorage.getItem('data'))[selectedProject].pages[index].name}
                </div>
              </div>
            )
          },
          position: {
            x: 400 * Math.cos((2 * Math.PI * index) / tempPages.length),
            y: 160 * Math.sin((2 * Math.PI * index) / tempPages.length)
          },
          targetPosition: 'top',
          sourcePosition: 'top'
        });
      });

      if (nodes.length > 0) {
        let minX = nodes[0].position.x;
        let minY = nodes[0].position.y;
        let maxX = nodes[0].position.x;
        let maxY = nodes[0].position.y;

        nodes.forEach((node, index3) => {
          if (index3 !== tempPages.length) {
            if (node.position.x < minX) {
              minX = node.position.x;
            }
            if (node.position.y < minY) {
              minY = node.position.y;
            }
            if (node.position.x > maxX) {
              maxX = node.position.x;
            }
            if (node.position.y > maxY) {
              maxY = node.position.y;
            }
          }
        });

        tempElements.push({
          id: '0',
          data: {
            label: (
              <div className="react-flow-node2">
                <div className="dot-row">
                  <span className="dot" />
                  <span className="dot" />
                  <span className="dot" />
                </div>
                <div className="react-flow-inside-box"> Href</div>
              </div>
            )
          },
          position: { x: minX - 200, y: minY },
          targetPosition: 'top',
          sourcePosition: 'top'
        });

        let countEdges = 0;
        let stroke;
        let text;

        tempPages.forEach((page, index2) => {
          if (page.actions !== undefined) {
            const countOutPerPage = {};

            page.actions.forEach((action) => {
              const indexTarget = ( action.internalAction !== undefined ? returnIndexOfPageName(
                action.internalAction.changePage,
                selectedProject
              ) : -1 );

              [stroke] = colorFlows;
              
              text = '';

              if( action.UIProps.type !== ''){
                text = `${action.UIProps.id  } : ${action.UIProps.type}`;
              }
              else{
                text = `${action.UIProps.id  } : undefined`;
              }
  
              if (nodes[indexTarget] !== undefined && nodes[index2] !== undefined) {
                if (
                  index2 - indexTarget === 1 ||
                  index2 - indexTarget === -1 ||
                  index2 - indexTarget === tempPages.length - 1 ||
                  index2 - indexTarget === -(tempPages.length - 1)
                ) {
                  if (
                    (nodes[index2].position.x - 0.1 < minX &&
                      nodes[indexTarget].position.x - 0.1 < minX) ||
                    (nodes[index2].position.x + 0.1 > maxX &&
                      nodes[indexTarget].position.x + 0.1 > maxX)
                  ) {
                    if (index2 !== indexTarget) {
                      if (countOutPerPage[indexTarget] === undefined) {
                        countOutPerPage[indexTarget] = 1;
                      } else {
                        countOutPerPage[indexTarget] += 1;
                      }

                      countEdges += 1;
                      if (nodes[index2].position.y >= nodes[indexTarget].position.y) {
                        tempElements.push({
                          id: `e${  countEdges.toString()}`,
                          source: (index2 + 1).toString(),
                          target: (indexTarget + 1).toString(),
                          type: 'customUp',
                          data: {
                            text,
                            distance: (countOutPerPage[indexTarget] - 1) * 50,
                            stroke,
                            positionStart: 'top',
                            positionTarget: 'bottom',
                            nodesWidth: nodes[0].__rf.width,
                            nodesHeight: nodes[0].__rf.height
                          }
                        });
                      } else if (nodes[index2].position.y < nodes[indexTarget].position.y) {
                        tempElements.push({
                          id: `e${  countEdges.toString()}`,
                          source: (index2 + 1).toString(),
                          target: (indexTarget + 1).toString(),
                          type: 'customDown',
                          data: {
                            text,
                            distance: countOutPerPage[indexTarget] * 50,
                            stroke,
                            positionStart: 'bottom',
                            positionTarget: 'top',
                            nodesWidth: nodes[0].__rf.width,
                            nodesHeight: nodes[0].__rf.height
                          }
                        });
                      }
                    }
                  } else if (
                    nodes[index2].position.x - 0.01 > minX &&
                    nodes[indexTarget].position.x - 0.01 > minX &&
                    nodes[index2].position.x + 0.01 < maxX &&
                    nodes[indexTarget].position.x + 0.01 < maxX
                  ) {
                    if (index2 !== indexTarget) {
                      if (countOutPerPage[indexTarget] === undefined) {
                        countOutPerPage[indexTarget] = 1;
                      } else {
                        countOutPerPage[indexTarget] += 1;
                      }

                      countEdges += 1;

                      if (nodes[index2].position.x >= nodes[indexTarget].position.x) {
                        tempElements.push({
                          id: `e${  countEdges.toString()}`,
                          source: (index2 + 1).toString(),
                          target: (indexTarget + 1).toString(),
                          type: 'customUp',
                          data: {
                            text,
                            distance: (countOutPerPage[indexTarget] - 1) * 50,
                            stroke,
                            positionStart: 'left',
                            positionTarget: 'right',
                            nodesWidth: nodes[0].__rf.width,
                            nodesHeight: nodes[0].__rf.height
                          }
                        });
                      } else if (nodes[index2].position.x < nodes[indexTarget].position.x) {
                        tempElements.push({
                          id: `e${  countEdges.toString()}`,
                          source: (index2 + 1).toString(),
                          target: (indexTarget + 1).toString(),
                          type: 'customDown',
                          data: {
                            text,
                            distance: countOutPerPage[indexTarget] * 50,
                            stroke,
                            positionStart: 'right',
                            positionTarget: 'left',
                            nodesWidth: nodes[0].__rf.width,
                            nodesHeight: nodes[0].__rf.height
                          }
                        });
                      }
                    }
                  } else if (
                    nodes[index2].position.x - 0.01 < minX ||
                    nodes[index2].position.x + 0.01 > maxX ||
                    nodes[indexTarget].position.x + 0.01 > maxX ||
                    nodes[indexTarget].position.x - 0.01 < minX
                  ) {
                    if (index2 !== indexTarget) {
                      if (countOutPerPage[indexTarget] === undefined) {
                        countOutPerPage[indexTarget] = 1;
                      } else {
                        countOutPerPage[indexTarget] += 1;
                      }

                      countEdges += 1;

                      if (nodes[index2].position.y <= nodes[indexTarget].position.y) {
                        let positionStart = '';
                        let positionTarget = '';

                        if (nodes[index2].position.x - 0.01 < minX) {
                          positionStart = 'bottom';
                          positionTarget = 'left';
                        } else if (nodes[index2].position.x + 0.01 > maxX) {
                          positionStart = 'bottom';
                          positionTarget = 'right';
                        } else if (nodes[indexTarget].position.x - 0.01 < minX) {
                          positionStart = 'left';
                          positionTarget = 'top';
                        } else if (nodes[indexTarget].position.x + 0.01 > maxX) {
                          positionStart = 'right';
                          positionTarget = 'top';
                        }

                        tempElements.push({
                          id: `e${  countEdges.toString()}`,
                          source: (index2 + 1).toString(),
                          target: (indexTarget + 1).toString(),
                          type: 'customUp',
                          data: {
                            text,
                            distance: (countOutPerPage[indexTarget] - 1) * 50,
                            stroke,
                            positionStart,
                            positionTarget,
                            nodesWidth: nodes[0].__rf.width,
                            nodesHeight: nodes[0].__rf.height
                          }
                        });
                      } else if (nodes[index2].position.y > nodes[indexTarget].position.y) {
                        let positionStart = '';
                        let positionTarget = ' ';

                        if (nodes[index2].position.x - 0.01 < minX) {
                          positionStart = 'top';
                          positionTarget = 'left';
                        } else if (nodes[index2].position.x + 0.01 > maxX) {
                          positionStart = 'top';
                          positionTarget = 'right';
                        } else if (nodes[indexTarget].position.x - 0.01 < minX) {
                          positionStart = 'left';
                          positionTarget = 'bottom';
                        } else if (nodes[indexTarget].position.x + 0.01 > maxX) {
                          positionStart = 'right';
                          positionTarget = 'bottom';
                        }

                        tempElements.push({
                          id: `e${  countEdges.toString()}`,
                          source: (index2 + 1).toString(),
                          target: (indexTarget + 1).toString(),
                          type: 'customDown',
                          data: {
                            text,
                            distance: countOutPerPage[indexTarget] * 50,
                            stroke,
                            positionStart,
                            positionTarget,
                            nodesWidth: nodes[0].__rf.width,
                            nodesHeight: nodes[0].__rf.height
                          }
                        });
                      }
                    }
                  }
                } else {
                  let positionStart = '';
                  let positionTarget = '';

                  if (nodes[index2].position.x - 0.01 < minX) {
                    positionStart = 'right';
                  } else if (nodes[index2].position.x + 0.01 > maxX) {
                    positionStart = 'left';
                  } else if (nodes[index2].position.y > 0) {
                    positionStart = 'top';
                  } else {
                    positionStart = 'bottom';
                  }

                  if (nodes[indexTarget].position.x - 0.01 < minX) {
                    positionTarget = 'right';
                  } else if (nodes[indexTarget].position.x + 0.01 > maxX) {
                    positionTarget = 'left';
                  } else if (nodes[indexTarget].position.y > 0) {
                    positionTarget = 'top';
                  } else {
                    positionTarget = 'bottom';
                  }

                  if (index2 !== indexTarget) {
                    if (countOutPerPage[indexTarget] === undefined) {
                      countOutPerPage[indexTarget] = 1;
                    } else {
                      countOutPerPage[indexTarget] += 1;
                    }

                    countEdges += 1;

                    if (nodes[index2].position.y <= nodes[indexTarget].position.y) {
                      tempElements.push({
                        id: `e${  countEdges.toString()}`,
                        source: (index2 + 1).toString(),
                        target: (indexTarget + 1).toString(),
                        type: 'customUp',
                        data: {
                          text,
                          distance: (countOutPerPage[indexTarget] - 1) * 50,
                          stroke,
                          positionStart,
                          positionTarget,
                          nodesWidth: nodes[0].__rf.width,
                          nodesHeight: nodes[0].__rf.height
                        }
                      });
                    } else if (nodes[index2].position.y > nodes[indexTarget].position.y) {
                      tempElements.push({
                        id: `e${  countEdges.toString()}`,
                        source: (index2 + 1).toString(),
                        target: (indexTarget + 1).toString(),
                        type: 'customDown',
                        data: {
                          text,
                          distance: countOutPerPage[indexTarget] * 50,
                          stroke,
                          positionStart,
                          positionTarget,
                          nodesWidth: nodes[0].__rf.width,
                          nodesHeight: nodes[0].__rf.height
                        }
                      });
                    }
                  }
                }
              }

              if (action.externalAction.linkHref !== '' && action.externalAction.linkHref !== '#' && action.externalAction.linkHref !== null) {
                if (countOutPerPage[100] === undefined) {
                  countOutPerPage[100] = 1;
                } else {
                  countOutPerPage[100] += 1;
                }

                [,stroke]= colorFlows;
                text = '';

                if( action.UIProps.type !== ''){
                  text = `${action.UIProps.id  } : ${action.UIProps.type} , href : ${  action.externalAction.linkHref}`;
                }
                else{
                  text = `${action.UIProps.id  } : undefined , href : ${  action.externalAction.linkHref}`;
                }

                tempElements.push({
                  id: `eOutHref${  index2  }page${  countOutPerPage[100].toString()}`,
                  source: (index2 + 1).toString(),
                  target: '0',
                  type: 'customUp',
                  data: {
                    text,
                    distance: (countOutPerPage[100] - 1) * 50,
                    stroke,
                    positionStart: 'left',
                    positionTarget: 'right',
                    nodesWidth: nodes[0].__rf.width,
                    nodesHeight: nodes[0].__rf.height,
                    isHref: true
                  },
                  arrowHeadType: 'arrowclosed'
                });
              } else if (
                action.service !== undefined &&
                action.service.url !== undefined &&
                action.service.url !== ''
              ) {
                if (countOutPerPage[200] === undefined) {
                  countOutPerPage[200] = 1;
                } else {
                  countOutPerPage[200] += 1;
                }

                [,,stroke] = colorFlows;
                text =
                  `${action.UIProps.id 
                  } : ${ 
                  action.UIProps.tagName 
                  } ,type request : ${ 
                  action.service.typeRequest 
                  } ,url : ${ 
                  action.service.url}`;

                tempElements.push({
                  id: `eOutHref${  index2  }page${  countOutPerPage[200].toString()}`,
                  target: (index2 + 1).toString(),
                  source: '0',
                  type: 'customDown',
                  data: {
                    text,
                    distance: countOutPerPage[200] * 50,
                    stroke,
                    positionStart: 'right',
                    positionTarget: 'left',
                    nodesWidth: nodes[0].__rf.width,
                    nodesHeight: nodes[0].__rf.height,
                    isService: true
                  },
                  arrowHeadType: 'arrowclosed'
                });
              }
            });
          }
        });
      }

      setElements(tempElements);
      setInitElements(true);
    }
  }, [selectedPage, selectedProject, initElements,loadPages]);

  // This useEffect is runned every time that dimensions of our window are changed and sets center of our canvas.
  useEffect(() => {
    const { nodes } = store.getState();
    if (nodes.length && reactflowInstance === true) {
      const zoom = 1;
      setCenter(0, 50, zoom);
      setInitElements(false);
    }
  }, [initElements, reactflowInstance]);

  // This useEffect contains a listener that fires the previous useEffect to reset the center of our diagramm.
  useEffect(() => {
    const resizeListener = () => {
      setInitElements(true);
    };
    // set resize listener
    window.addEventListener('resize', resizeListener);
    // clean up function
    return () => {
      // remove resize listener
      window.removeEventListener('resize', resizeListener);
    };
  }, []);

  // Custom edges.
  const edgeTypes = {
    customDown: CustomEdgeDown,
    customUp: CustomEdgeUp
  };

  const onLoad = () => {
    setReactflowInstance(true);
  };

  const myErrorHandler = (error, info) => {
    throw Error(`Error in PageFlowCanvas.js that has been caught by Error Boundary${error}
      More info about this error: ${info}
    `);
  }

  return (
    <div
      className={clsx('blueBorderStyle', 'flexPropsDataCanvas')}
      style={{ backgroundColor: 'white', flexDirection: 'row' }}
    >
      <div
        style={{
          height: 'auto',
          display: 'flex',
          flexWrap: 'wrap',
          width: '100%',
          overflowY: 'auto'
        }}
      >
        {selectedProject > -1 ? (
          <ErrorBoundary onError={myErrorHandler}>
            <ReactFlow
              elements={elements}
              elementsSelectable={false}
              nodesConnectable={false}
              nodesDraggable
              zoomOnScroll={false}
              panOnScroll={false}
              panOnScrollMode={false}
              zoomOnDoubleClick={false}
              onConnect={false}
              onElementClick={false}
              onNodeDragStart={undefined}
              onNodeDragStop={undefined}
              paneMoveable={false}
              onPaneClick={undefined}
              onPaneScroll={undefined}
              onPaneContextMenu={undefined}
              defaultZoom={1}
              edgeTypes={edgeTypes}
              minZoom={1}
              maxZoom={1}
              snapToGrid
              onLoad={onLoad}
            >
              <PageFlowSideBar colorFlows={colorFlows} />
            </ReactFlow>
          </ErrorBoundary>
        ) : null}
      </div>
    </div>
  );
};

export default ProjectFlowCanvas;
